import * as React from "react";
import { cn } from "../_internal/cn";
import type { TypeNarrativeDataCalloutProps } from "./NarrativeDataCalloutTypes";

/**
 * NarrativeDataCallout — a horizontally oriented callout that pairs an injected
 * data-viz visual (left, ~1/3 width) with a block of body copy (right, ~2/3).
 *
 * The kit ships no chart of its own; the `visual` slot accepts any node — the
 * stories drop in a v2 `DonutChart` from `@sproutsocial/seeds-react-data-viz`.
 * narrative-kit takes no runtime dependency on data-viz (mirrors MetricHighlight's
 * `sparkline` slot).
 *
 * Renders inner content only — it carries no surface, shadow, or accent of its
 * own. Drop it into a `NarrativeContainer` (see the stories) to reproduce the
 * card look.
 *
 * Layout is a flex row driven by a container query, so it collapses to a stacked
 * column (visual on top, text below) based on the width the component is actually
 * given, not the viewport.
 *
 * Styled via `narrative-data-callout.css` (Seeds CSS custom properties); consumers
 * import the classes through `@sproutsocial/racine/css/components`. Overrides come
 * via standard `className` / `style`.
 */
const NarrativeDataCallout = React.forwardRef<
  HTMLDivElement,
  TypeNarrativeDataCalloutProps
>(({ visual, children, visualLabel, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("seeds-narrative-data-callout", className)}
    {...props}
  >
    <div className="seeds-narrative-data-callout-row">
      <div
        className="seeds-narrative-data-callout-visual"
        role={visualLabel != null ? "img" : undefined}
        aria-label={visualLabel}
      >
        {visual}
      </div>
      <div className="seeds-narrative-data-callout-body">{children}</div>
    </div>
  </div>
));

NarrativeDataCallout.displayName = "NarrativeDataCallout";

export default NarrativeDataCallout;
